import React, { useState } from 'react';
import { Heart, MessageCircle, Share2, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import CommentsSection from './CommentsSection';
import ShareSheet from './ShareSheet';
import { toast } from 'sonner';

/**
 * ContentReactBar — the universal like/comment/share strip used on every
 * card and non-post detail view (soundscapes, breathing exercises, manifest
 * affirmations). Backed by the existing `/api/comments/*` and
 * `/api/user/like-post` endpoints, which treat their id fields as opaque
 * strings — so passing `soundscape:soft-rain` or `manifest:healing:3` works
 * without any backend changes.
 *
 * Props
 *   contentRef  — opaque string identifier for this piece of content.
 *                 Convention: "<kind>:<id>[:<index>]" (post:xyz,
 *                 soundscape:soft-rain, breath:box-breath, manifest:healing:3).
 *   share       — object shaped for ShareSheet's `content` prop
 *                 { kind, id, title, text, url, recommendable? }.
 *                 If omitted, the share button is hidden.
 *   size        — 'sm' | 'md' (default 'md')
 *   variant     — 'onDark' | 'onLight' (default 'onLight')
 *   onOpenAuth  — optional callback if the user needs to sign in; default
 *                 just toasts "sign in to like".
 *   showCommentButton — hide the comment icon when false (default true)
 */
const SIZE = {
  sm: { icon: 'w-3.5 h-3.5', pill: 'w-8 h-8', gap: 'gap-1.5', text: 'text-[10px]' },
  md: { icon: 'w-4 h-4',      pill: 'w-9 h-9', gap: 'gap-2',   text: 'text-[11px]' },
};

const VARIANT = {
  onLight: {
    idle: 'bg-white/70 border-stone-200 text-stone-600 hover:bg-white',
    active: 'bg-red-50 border-red-200 text-red-500',
  },
  onDark: {
    idle: 'bg-white/12 border-white/25 text-white/85 hover:bg-white/22',
    active: 'bg-red-500/25 border-red-400/60 text-red-300',
  },
};

const ContentReactBar = ({
  contentRef,
  share,
  size = 'md',
  variant = 'onLight',
  showCommentButton = true,
  className = '',
}) => {
  const { user, likedPosts, toggleLikePost } = useAuth();
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const isLiked = (likedPosts || []).includes(contentRef);
  const s = SIZE[size] || SIZE.md;
  const v = VARIANT[variant] || VARIANT.onLight;

  const stopBubble = (e) => {
    // Prevents the parent <Link> or card <button> from firing when the user
    // taps a react icon.
    e.preventDefault();
    e.stopPropagation();
  };

  const handleLike = (e) => {
    stopBubble(e);
    if (!user) {
      toast('Sign in to like reflections', { description: 'Track your favourites and grow your streak.' });
      return;
    }
    toggleLikePost(contentRef);
  };

  const handleComment = (e) => {
    stopBubble(e);
    setCommentsOpen(true);
  };

  const handleShare = (e) => {
    stopBubble(e);
    setShareOpen(true);
  };

  const iconBtn = (active) => (
    `${s.pill} rounded-full flex items-center justify-center border transition-all active:scale-95 ${active ? v.active : v.idle}`
  );

  return (
    <>
      <div
        className={`inline-flex items-center ${s.gap} ${className}`}
        data-testid={`content-react-bar-${contentRef}`}
        onClick={stopBubble}
      >
        <button
          type="button"
          onClick={handleLike}
          aria-pressed={isLiked}
          aria-label={isLiked ? 'Unlike' : 'Like'}
          data-testid={`react-like-${contentRef}`}
          className={iconBtn(isLiked)}
          style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
        >
          <Heart className={`${s.icon} ${isLiked ? 'fill-current' : ''}`} />
        </button>

        {showCommentButton && (
          <button
            type="button"
            onClick={handleComment}
            aria-label="Comments"
            data-testid={`react-comment-${contentRef}`}
            className={iconBtn(false)}
            style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
          >
            <MessageCircle className={s.icon} />
          </button>
        )}

        {share && (
          <button
            type="button"
            onClick={handleShare}
            aria-label="Share"
            data-testid={`react-share-${contentRef}`}
            className={iconBtn(false)}
            style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
          >
            <Share2 className={s.icon} />
          </button>
        )}
      </div>

      {commentsOpen && (
        <div
          className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm px-3 pb-3"
          role="dialog"
          aria-modal="true"
          onClick={(e) => { if (e.target === e.currentTarget) setCommentsOpen(false); }}
          data-testid={`react-comments-modal-${contentRef}`}
        >
          <div className="bg-[#FFFCF3] w-full max-w-[420px] rounded-3xl shadow-2xl border border-stone-200 max-h-[85vh] overflow-y-auto animate-scale-in">
            <div className="sticky top-0 flex items-center justify-between px-5 py-3 bg-[#FFFCF3] border-b border-stone-200/70">
              <h3
                className="text-sm font-semibold text-stone-800"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Comments
              </h3>
              <button
                type="button"
                onClick={() => setCommentsOpen(false)}
                aria-label="Close comments"
                className="w-8 h-8 rounded-full flex items-center justify-center text-stone-500 hover:text-stone-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-5 pb-5 pt-3">
              <CommentsSection postId={contentRef} />
            </div>
          </div>
        </div>
      )}

      {share && (
        <ShareSheet
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          content={share}
        />
      )}
    </>
  );
};

export default ContentReactBar;
